/**
 * sentry.js — Frontend Sentry initialization for the Expo web app.
 *
 * Initializes Sentry before the app renders so that render errors,
 * unhandled promise rejections, and network failures are captured.
 * The DSN is read from EXPO_PUBLIC_SENTRY_DSN (safe to ship in the
 * public bundle — Sentry public DSNs are designed for client-side use).
 *
 * If no DSN is configured, init is a no-op — the app runs normally.
 */

import * as Sentry from '@sentry/react-native';

let _initialized = false;

export function initSentry() {
  if (_initialized) return;
  _initialized = true;

  const dsn = process.env.EXPO_PUBLIC_SENTRY_DSN;
  if (!dsn) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[sentry] EXPO_PUBLIC_SENTRY_DSN not set — error tracking disabled');
    }
    return;
  }

  // Derive release version from app config
  let release = null;
  try {
    const appConfig = require('./app.config');
    const config = typeof appConfig === 'function' ? appConfig({ config: {} }) : appConfig;
    release = config?.expo?.version || config?.version || null;
  } catch {}

  // Determine environment
  const environment = process.env.NODE_ENV === 'production' ? 'production'
    : process.env.NODE_ENV === 'staging' ? 'staging'
    : 'development';

  Sentry.init({
    dsn,
    environment,
    release: release ? `bet-optimized@${release}` : undefined,
    // Sample 10% of transactions for performance monitoring
    tracesSampleRate: 0.1,
    // Don't send PII
    sendDefaultPii: false,
    // Ignore common non-actionable errors
    ignoreErrors: [
      'Network request failed',
      'Failed to fetch dynamically imported module',
      'ResizeObserver loop limit exceeded',
      'Loading chunk',
    ],
    // Only enable in production/staging — skip in dev to avoid noise
    enabled: environment !== 'development' || !!dsn,
  });

  if (process.env.NODE_ENV !== 'production') {
    console.log(`[sentry] Initialized — env=${environment}, release=${release || 'unknown'}`);
  }
}

/**
 * React Error Boundary component that captures render crashes.
 * Wrap the root app component with this.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary || null;

export { Sentry };
export { captureException } from '@sentry/react-native';
export { captureMessage } from '@sentry/react-native';
